import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { cart } from '@sorare/core/src/lib/glossary';
import { ClickAddToCart_Origin } from '@sorare/core/src/protos/events/so5/shared/events';

import { CollectionPrediction } from '#src/components/card/CollectionPrediction/index.tsx';
import { FlexCard } from '#src/components/token/FlexCard/index.tsx';
import { useCartEvents } from '#src/hooks/events/useCartEvents.ts';
import { useIsPrimaryBuyLive } from '#src/hooks/primaryBuy/useIsPrimaryBuyLive.ts';
import { useCart } from '#src/hooks/useCart.ts';

import type { CollectionUpsellCard_anyCard } from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical)`
  max-width: 190px;
  width: 100%;
  flex-shrink: 0;
  align-items: center;
  padding: var(--double-unit);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-150);
`;

type Props = {
  anyCard: CollectionUpsellCard_anyCard;
};

export const CollectionUpsellCard = ({ anyCard }: Props) => {
  const primaryBuy = useIsPrimaryBuyLive(anyCard);
  const { addCardsToCart } = useCart();
  const { trackClickAddToCart } = useCartEvents({ anyCard });
  if (!primaryBuy) return null;

  return (
    <Wrapper className="text-center">
      <FlexCard card={anyCard} width={160} />
      <CollectionPrediction card={anyCard} placement="top" />
      <AmountWithConversion monetaryAmount={primaryBuy.price} column />
      <Button
        size="small"
        color="quaternary"
        onClick={async () => {
          trackClickAddToCart(ClickAddToCart_Origin.CART_UPSELL);
          return addCardsToCart.mutate(anyCard.sport, [anyCard.slug]);
        }}
      >
        <FormattedMessage {...cart.addToCart} />
      </Button>
    </Wrapper>
  );
};

CollectionUpsellCard.fragments = {
  anyCard: gql`
    fragment CollectionUpsellCard_anyCard on AnyCardInterface {
      slug
      ...FlexCard_anyCard
      ...CollectionPrediction_anyCard
      ...useIsPrimaryBuyLive_anyCard
      ...useCart_anyCard
      ...useCartEvents_anyCard
    }
    ${FlexCard.fragments.anyCard}
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${CollectionPrediction.fragments.anyCard}
    ${useCart.fragments.anyCard}
    ${useCartEvents.fragments.anyCard}
  ` as TypedDocumentNode<CollectionUpsellCard_anyCard>,
};
